import React from 'react';

import List from '@mui/material/List';
import makeStyles from '@mui/styles/makeStyles';
import { ItemMetaData } from 'layouts/Portal/sidebar/menuItemsConfig';

import PaperContainer from '@fd/ui/Layout/PaperContainer';
import ListItemLinkButton from '@fd/ui/List/ListItemLinkButton';

import { shouldDisplay, usePortalUser } from '../../viewConfiguration';
import { type BadgeLabel } from '../atoms/Badge';

export type SettingsMenuItem = ItemMetaData & {
  subtitle?: ItemMetaData['title'];
  tagText?: ItemMetaData['title'];
  labels?: BadgeLabel[];
  isDangerZone?: boolean;
  propertyId?: string;
  propertyName?: string;
  salesChannelName?: string;
  salesChannelId?: string;
  salesChannelType?: string;
  dataFd?: string;
};

const useStyles = makeStyles(() => ({
  listItemDanger: {
    color: '#ff426a',
  },
}));

const renderListItem = (
  item: SettingsMenuItem,
  shouldShowAsLast: boolean,
  classes: Record<string, string>
) => {
  return (
    <ListItemLinkButton
      key={item.path}
      title={item.title}
      subtitle={item.subtitle}
      iconComponent={item.icon}
      link={item.path}
      tagText={item.tagText}
      labels={item.labels}
      isLast={shouldShowAsLast}
      dataFd={item.dataFd}
      classesFromParent={
        item.isDangerZone
          ? {
              logo: classes.listItemDanger,
              primary: classes.listItemDanger,
              secondary: classes.listItemDanger,
            }
          : undefined
      }
    />
  );
};

const SettingsList = ({
  menuItems,
  shouldShowAsLast,
}: {
  menuItems: SettingsMenuItem[];
  shouldShowAsLast?: (item: SettingsMenuItem, index: number) => boolean;
}) => {
  const classes = useStyles();
  const portalUser = usePortalUser();
  const visibleItems = menuItems.filter((item) =>
    shouldDisplay(portalUser, item.viewId, {
      legacyVisibilityPermissions: item.permissions,
      exemptFromViewConfiguration: item.exemptFromViewConfiguration,
    })
  );

  return (
    <PaperContainer fluid>
      <List data-fd="settings-list">
        {visibleItems.map((item, index) => {
          const isLast =
            index === visibleItems.length - 1 || Boolean(shouldShowAsLast?.(item, index));
          return renderListItem(item, isLast, classes);
        })}
      </List>
    </PaperContainer>
  );
};

export default SettingsList;
